/**
 * Localized country display names for the webshop address dropdowns.
 *
 * The backend returns each operating country's ISO alpha-2 `country_code` plus its English
 * `country_name`. The shop is multilingual, so the dropdown must show the country in the UI
 * language AND be sorted in that language's alphabetical order — otherwise switching to e.g.
 * German leaves the list in English names/order.
 *
 * We derive the localized name from the ISO code via the browser's `Intl.DisplayNames`
 * (no translation files, no DB change), falling back to the English `country_name` (then the
 * code) if the runtime can't localize a given code.
 */

/**
 * The app uses some non-standard i18n codes (notably `ge` for German and `ct` for the
 * clear-text pseudo-language, alongside the valid `en`/`es`/`pl`/`ru`). `Intl.DisplayNames` and
 * `localeCompare` need real BCP-47 tags, so map the app code to a locale first — otherwise
 * `Intl.DisplayNames(['ge'])` fails to localize and we fall back to the English name.
 */
const APP_LANG_TO_LOCALE: Record<string, string> = {ge: 'de', ct: 'en'}
function toLocale(lang: string | undefined): string {
  return APP_LANG_TO_LOCALE[lang || ''] || lang || 'en'
}

export interface CountryLike {
  id: number | string
  country_code: string
  country_name?: string
  status?: number
  [key: string]: any
}

/** The localized region name for an ISO alpha-2 code, or a sensible fallback. */
export function localizedCountryName(
  countryCode: string | undefined,
  lang: string,
  fallback?: string
): string {
  const code = (countryCode || '').toUpperCase()
  // `Intl.DisplayNames` is available in every browser we target, but its TS types need a recent
  // `lib`; cast through `any` so this compiles regardless of the project's tsconfig lib setting.
  const DisplayNames = (Intl as any).DisplayNames
  if (code.length === 2 && typeof DisplayNames === 'function') {
    try {
      const name = new DisplayNames([toLocale(lang)], {type: 'region'}).of(code)
      if (name && name !== code) return name
    } catch {
      /* unsupported locale/runtime — fall through */
    }
  }
  return fallback || countryCode || ''
}

/**
 * Returns the countries with a localized `display_name`, sorted alphabetically in `lang`.
 * Pure — callers typically wrap it in a memo keyed on (countries, lang).
 */
export function sortCountriesByLocale<T extends CountryLike>(
  countries: T[],
  lang: string
): Array<T & {display_name: string}> {
  const locale = toLocale(lang)
  return countries
    .map((c) => ({...c, display_name: localizedCountryName(c.country_code, lang, c.country_name)}))
    .sort((a, b) => a.display_name.localeCompare(b.display_name, locale))
}
